/* eslint-disable @typescript-eslint/no-unused-vars */
import { FormProvider, useForm } from 'react-hook-form';
import { FormButton } from '@portal_components/components/FormButton';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Appointment, BulkAssignFileForm, Contract, CustomerGroup, MeterPointDetails, DuplicateHandoverCheckResponse } from '../types';
import { BulkAssignForm } from '../elements/BulkAssign/BulkAssignForm';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { FormFileInput } from '@portal_components/components/FormFileInput';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { usePortalQuery, EHttpMethod } from '../hooks/usePortalQuery';
import { FormErrorList } from '@portal_components/components/FormErrorList';
import { EnvironmentVariables } from '../EnvironmentVariables';
import { DateTime } from 'luxon';
import { Button } from '@portal_components/components/Button';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '@portal_components/components/Modal';
import { EUserRole } from '@portal_components/types';
import { useUserInfo } from '../hooks/useUserInfo';
import { PageHeading } from '@portal_components/components/PageHeading';
import { checkMpanDigitSum, normalizeMpan } from '@portal_components/validation/validationUtils';

const validateFileList = (fileList: FileList): boolean => {
  return fileList.length === 1;
};

const validationSchema = Yup.object<
  BulkAssignFileForm,
  {
    [Key in keyof BulkAssignFileForm]: Yup.AnySchema<BulkAssignFileForm[Key]>;
  }
>({
  fileList: Yup.mixed<FileList>().required().test('fileList', 'File must be selected', validateFileList),
  customerGroupId: Yup.string().required('Field is required'),
  contractId: Yup.string().required('Field is required')
});

export const BulkAssignPage = () => {
  const { userInfo } = useUserInfo();
  const [fileSiteList, setFileSiteList] = useState<Partial<MeterPointDetails>[] | null>(null);
  const [handoverFileName, setHandoverFileName] = useState<string | undefined>(undefined);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [disableDuplicateFileCheck, setDisableDuplicateFileCheck] = useState<boolean>(false);
  const [disableGetBulkAppointments, setDisableGetBulkAppointments] = useState<boolean>(true);
  const [showConfirmFormReset, setShowConfirmFormReset] = useState<boolean>(false);
  const [showConfirmFileReload, setShowConfirmFileReload] = useState<boolean>(false);
  const [localAppointments, setLocalAppointments] = useState<{ [mprn: string]: Appointment }>({});

  const queryClient = useQueryClient();

  const { data: customerGroups, isLoading: customerGroupsLoading } = usePortalQuery<CustomerGroup[]>(
    EHttpMethod.GET,
    'customer-groups',
    'customer-groups',
    true,
    undefined,
    undefined,
    undefined,
    undefined,
    600000,
    600000
  );

  const methods = useForm<BulkAssignFileForm>({
    values: undefined,
    resolver: yupResolver<BulkAssignFileForm>(validationSchema as any)
  });

  const [customerGroupId, contractId] = methods.watch(['customerGroupId', 'contractId']);

  useEffect(() => {
    if (!customerGroupId) return;
    methods.setValue('contractId', '');
  }, [customerGroupId, methods]);

  const { data: customerContracts, isLoading: customerContractsLoading } = usePortalQuery<Contract[]>(
    EHttpMethod.GET,
    `customer-contracts/${customerGroupId}?filter=Y`,
    `customer-contracts/${customerGroupId}?filter=Y`,
    !!customerGroupId
  );

  const handleSubmit = async (data: BulkAssignFileForm, performDuplicateCheck: boolean) => {
    setFileSiteList(null);
    setHandoverFileName(undefined);
    const fileName = data.fileList[0]?.name;

    if (!fileName || fileName.endsWith('.csv') === false) {
      setErrorMessage('Please upload a valid CSV file');
      return;
    }

    setFormSubmitted(true);

    if (performDuplicateCheck) {
      setDisableDuplicateFileCheck(false);
    }

    const selectedContract = customerContracts?.find(contract => contract.contractId === contractId);
    if (!selectedContract) return;
    const contractStartDate = DateTime.fromISO(selectedContract.contractStartDate).toUTC(undefined, { keepLocalTime: true }).startOf('day');
    const currentDate = DateTime.now().toUTC(undefined, { keepLocalTime: true }).startOf('day');
    const defaultDate = (contractStartDate.isValid && contractStartDate > currentDate ? contractStartDate : currentDate).toISO({
      suppressMilliseconds: true
    });

    const fileText = await data.fileList[0].text();
    const records: MeterPointDetails[] = [];
    const lines = fileText.split('\n');
    lines.shift(); // remove header
    lines.pop(); // remove last empty line
    for (const line of lines) {
      const [
        mprn,
        ,
        ,
        ,
        siteReferenceNumber,
        propertyNameNumber,
        addressLine1,
        addressLine2,
        addressLine3,
        town,
        county,
        postcode,
        siteContactTitle,
        siteContactFirstname,
        siteContactSurname,
        siteContactNumber,
        siteContactExtensionNumber,
        siteContactNumber2,
        emailAddress,
        meterWorkRequired,
        custOwnedComms,
        ,
        ,
        siteTypeId,
        ,
        ,
        ,
        ,
        ,
        ,
        ,
        ,
        ,
        relatedMprn,
        ,
        ,
        ,
        ,
        ,
        ,
        batchName,
        ,
        ,
        ,
        ,
        ,
        ,
      ] = line.split(',').map(item => item.trim());

      const correctBatchName = data.batchName != null && data.batchName !== '' ? data.batchName : batchName;

      if (!mprn) {
        continue;
      }
      const normalizedMprn = normalizeMpan(mprn);
      if (!/^\d{13}$/.test(normalizedMprn) || !checkMpanDigitSum(normalizedMprn)) {
        continue;
      }

      records.push({
        mprn: normalizedMprn,
        siteTypeId: siteTypeId || undefined,
        siteReferenceName: siteReferenceNumber,
        meterWorkRequired: meterWorkRequired || undefined,
        custOwnedComms: (custOwnedComms ? custOwnedComms : undefined) as 'Y' | 'N' | undefined,
        batchName: correctBatchName,
        relatedMprn: relatedMprn,
        noCommsId: undefined,
        assetOwnership: undefined,
        isExport: 'N',
        isImport: 'Y',
        utilityId: selectedContract.contractTypeCode,
        effectiveDate: defaultDate,
        address: {
          addressName: propertyNameNumber,
          addressLine1,
          addressLine2,
          addressLine3,
          town,
          county,
          postcode
        },
        contact: {
          siteContactTitle,
          siteContactFirstname,
          siteContactSurname,
          siteContactNumber,
          siteContactExtensionNumber,
          emailAddress,
          mobileNumber: siteContactNumber2
        }
      });
    }
    if (records.length === 0) {
      setErrorMessage('No records found in the file');
      return;
    }

    setErrorMessage(null);

    setFileSiteList(records);
    setHandoverFileName(fileName);
  };

  const handleReloadClick = () => {
    setDisableDuplicateFileCheck(true);
    handleSubmit(methods.getValues(), false);
    setShowConfirmFileReload(false);
  };

  const handleResetClick = () => {
    const emptyFileList = new DataTransfer().files;

    queryClient.invalidateQueries({
      queryKey: ['get-appointment-bulk'],
      exact: false,
      refetchType: 'none'
    });

    setFileSiteList(null);
    setErrorMessage(null);
    setFormSubmitted(false);
    methods.reset({ fileList: emptyFileList, customerGroupId: '', contractId: '', batchName: '' });
    setDisableGetBulkAppointments(true);
    setShowConfirmFormReset(false);
  };

  const handleRefreshAppointments = () => {
    queryClient.invalidateQueries({
      queryKey: ['get-appointment-bulk'],
      exact: false,
      refetchType: 'active'
    });
  };

  const handleRemoveAppointment = (removedMprn: string) => {
    setLocalAppointments(prevAppointments => {
      const updatedAppointments = { ...prevAppointments };
      delete updatedAppointments[removedMprn];
      return updatedAppointments;
    });
  };

  //form submissions being set to false otherwise you are able to see contents of duplicate files
  //this wasn't clear to users and would cause problems.
  const { data: duplicateRes, isLoading: duplicateCheckLoading } = usePortalQuery<DuplicateHandoverCheckResponse>(
    EHttpMethod.GET,
    `check-duplicate-handover-file?handoverFileName=${encodeURIComponent(methods.getValues().fileList?.[0]?.name || '')}`,
    `check-duplicate-handover-file/${encodeURIComponent(methods.getValues().fileList?.[0]?.name || '')}`,
    !!fileSiteList && formSubmitted && methods.getValues().fileList.length > 0 && !disableDuplicateFileCheck,
    undefined,
    (isDuplicate: DuplicateHandoverCheckResponse) => {
      if (isDuplicate.duplicate) {
        //set as form submitted to allow resetForm button to activate.
        setFormSubmitted(true);
      } else {
        setDisableGetBulkAppointments(false);
      }
    },
    undefined,
    undefined,
    0,
    0
  );

  const { data: appointments, isLoading: appointmentsLoading } = usePortalQuery<{
    [mprn: string]: Appointment;
  }>(
    EHttpMethod.POST,
    `get-appointment/bulk`,
    `get-appointment-bulk`,
    !!fileSiteList && formSubmitted && !disableGetBulkAppointments,
    fileSiteList
      ? {
          contractId: contractId,
          mpans: fileSiteList
            .reduce<string[]>((acc, site) => {
              if (!site?.mprn) return acc;
              acc.push(normalizeMpan(site.mprn));
              return acc;
            }, [])
            .filter(m => /^\d{13}$/.test(m) && checkMpanDigitSum(m))
        }
      : undefined,
    data => {
      setLocalAppointments(data);
    }
  );

  const handleAddNewCustomer = () => {
    window.open(`${EnvironmentVariables.ContractPortalUrl}/addGroup`, '_blank');
  };

  if (!userInfo || userInfo.role === EUserRole.READONLY_USER) return <p className="text-center">You do not have permission to upload handover files.</p>;

  if (customerGroupsLoading) {
    return <div className="flex flex-row justify-center">Loading customer groups...</div>;
  }

  return (
    <div className="flex flex-col justify-start">
      <PageHeading
        title="Upload Handover Files"
        navigationButtons={[
          {
            title: 'Back',
            path: '/',
            icon: '/back.svg',
            className: 'w-6 h-6'
          }
        ]}
      />
      <div className="pb-4 mb-8 pl-2 border-b-2 border-b-secondary-deep-blue-80">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(data => handleSubmit(data, true))}>
            <div className="grid grid-cols-2 gap-2">
              <FormFileInput title="File" name="fileList" mandatory disabled={formSubmitted} readonly={formSubmitted} />
              <FormTextInput title="Batch Name" name="batchName" />
            </div>
            <div className="flex flex-row">
              <p className="text-sm pb-0.5 pl-0.5 cursor-pointer text-interactive-coral hover:text-interactive-coral-db-70" onClick={handleAddNewCustomer}>
                Click here to add a new customer
              </p>
              <img src="/externalLinkButtonActive.svg" alt="external-link" className="pl-1 w-4 h-4" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FormSearchDropdown
                title="Customer Group"
                name="customerGroupId"
                placeholder="Select Customer Group"
                elementsForDisplay={(customerGroups ?? []).map(({ customerGroupName, customerGroupId }) => ({
                  title: customerGroupName,
                  value: customerGroupId
                }))}
                mandatory
              />
              <FormDropdown
                title="Contract"
                name="contractId"
                placeholder="Select Contract"
                elementsForDisplay={(customerContracts ?? []).map(({ contractId, contractType }) => ({
                  title: `${contractType} (Contract ID: ${contractId})`,
                  value: contractId
                }))}
                mandatory
                readonly={!customerGroupId || customerContractsLoading || !customerContracts || customerContracts.length === 0}
              />
            </div>
            <div className="flex flex-row justify-between h-10 mt-4">
              <div className="flex flex-row gap-2">
                <Button text="Reload File" primary={false} disabled={!formSubmitted} onClick={() => setShowConfirmFileReload(true)} />
                <Button text="Reset Form" primary={false} disabled={!formSubmitted} onClick={() => setShowConfirmFormReset(true)} />
              </div>
              <FormButton type="submit" disabled={!contractId || formSubmitted} primary>
                Upload File
              </FormButton>
            </div>
            {(!customerContracts || customerContracts.length === 0) && customerGroupId && !customerContractsLoading && (
              <div className="pb-4 text-feedback-red inline-block">{'This customer group has no active contracts.'}</div>
            )}
            <FormErrorList />
          </form>
        </FormProvider>
        {errorMessage && <div className="pt-3 pb-3 text-feedback-red">{errorMessage}</div>}
      </div>

      {showConfirmFormReset && (
        <Modal
          primaryMessage="Are you sure you want to reset the form?"
          secondaryMessage="Any unsubmitted changes made since uploading the file will be lost."
          confirmButtonText="Yes"
          cancelButtonText="No"
          onConfirm={handleResetClick}
          onCancel={() => setShowConfirmFormReset(false)}
        />
      )}
      {showConfirmFileReload && (
        <Modal
          primaryMessage="Are you sure you want to reload the file?"
          secondaryMessage="Any unsubmitted changes made since uploading the file will be lost."
          confirmButtonText="Yes"
          cancelButtonText="No"
          onConfirm={handleReloadClick}
          onCancel={() => setShowConfirmFileReload(false)}
        />
      )}

      {formSubmitted && appointmentsLoading && !disableGetBulkAppointments && <div className="flex flex-row justify-center">Loading appointments...</div>}
      {formSubmitted && duplicateCheckLoading && <div className="flex flex-row justify-center">Loading Handover File...</div>}
      {duplicateRes?.duplicate && (
        <div className="pb-4 text-feedback-red">
          A handover file with the name <strong>{methods.getValues().fileList?.[0].name}</strong> already exists. Please rename the file and try again.
        </div>
      )}
      {formSubmitted && fileSiteList !== null && !appointmentsLoading && !!appointments && handoverFileName && !duplicateRes?.duplicate && !duplicateCheckLoading && (
        <BulkAssignForm
          siteList={fileSiteList}
          customerGroupId={customerGroupId}
          contractId={contractId}
          appointments={localAppointments}
          handoverFileName={handoverFileName}
          handleAppointmentRemove={handleRemoveAppointment}
          handleRefreshAppointments={handleRefreshAppointments}
        />
      )}
    </div>
  );
};
